import { hexToRgb } from '@mui/material';
import { endOfDay, parse, setHours, setMinutes } from 'date-fns';
import { tz } from '@date-fns/tz';
import {
    AwardExpiryLabelDetailStyles,
    AwardsScreenShapes,
    AwardTemplate
} from 'components/customers/models/Award';
import { LoyaltyProvider } from 'components/loyalty/enums';
import { IAwardEnrichment, IAwardEnrichmentCreate } from 'components/loyalty/models/AwardEnrichment';
import { RGBAValue } from 'lib/form/fields/ColorFormField';
import { rgbaToHex } from 'lib/helpers';
import { deriveExpiryLabelAlignment, deriveExpiryLabelAnchorStyle, getPerkOpacity } from '../helpers';
import { weekdays } from './consts';
import { AwardConfigureFormData } from './forms/AwardConfigureForm';
import { AwardGeneralFormData } from './forms/AwardGeneralForm';
import { awardRedemptionDefaultFormValues, AwardRedemptionFormData } from './forms/AwardRedemptionForm';
import { isBoolean, isDefined, isEmptyString, isNumber, isString } from 'lib/typeguards';
import { Perk, TextAlignment, VerticalAlignment, WalletPassType } from 'components/loyalty/models/PerkModel';
import { PerkSettingsFormData } from '../forms/PerkSettingsForm';
import { PerkVisualisationFormData } from 'components/perks/forms/PerkVisualisationForm';

export const getMinsSinceMidnight = (time: string): number | undefined => {
    if (!time) {
        return;
    }
    const dt = parse(time, 'HH:mm', new Date());
    return dt.getHours() * 60 + dt.getMinutes();
};

export const convertSecondsToTime = (seconds: number) => {
    let hours: number | string = Math.floor(seconds / 3600);
    let minutes: number | string = Math.floor((seconds - hours * 3600) / 60);

    if (hours < 10) {
        hours = '0' + hours;
    }
    if (minutes < 10) {
        minutes = '0' + minutes;
    }

    return hours + ':' + minutes;
};

const generateCreatePseudoAwardVisualizationBody = (
    settings: AwardConfigureFormData,
    awardsScreenAwardEnabled?: boolean
) => {
    const opacity = getPerkOpacity(settings.backgroundColor);

    return {
        visualisation: {
            subtitleText: settings.subtitle,
            subtitleTwoText: settings.subtitle2,
            textAlignment: settings.align,
            backgroundOverlayOpacity: opacity,
            backgroundOverlayColour: rgbaToHex(settings.backgroundColor, true),
            textColour: rgbaToHex(settings.textColor, true),
            backgroundImageUrl: settings.image ?? '',
            verticalAlignment: settings.valign,
            titleText: settings.titleText,
            showExpiry: settings.showExpiry ?? false,
            ...(settings.showExpiry
                ? {
                      expiryLabelAnchorStyle: deriveExpiryLabelAnchorStyle(
                          settings.expiryLabelAnchorStyleAlign,
                          settings.expiryLabelAnchorStyleVAlign
                      ),
                      expiryLabelDetailStyle: settings.expiryLabelDetailStyle
                  }
                : {}),
            ...(awardsScreenAwardEnabled && {
                awardsScreenShape: AwardsScreenShapes.DOUBLE_RECTANGLE
            })
        }
    };
};

const generateCreateAdHocVisualizationBody = (
    settings: AwardConfigureFormData,
    awardsScreenAwardEnabled?: boolean
) => {
    const opacity = getPerkOpacity(settings.backgroundColor);
    return {
        visualisation: {
            backgroundOverlayOpacity: opacity,
            backgroundOverlayColour: rgbaToHex(settings.backgroundColor, true),
            backgroundImageUrl: settings.image ?? '',
            ...(awardsScreenAwardEnabled && settings.awardsScreenShape !== AwardsScreenShapes.DOUBLE_RECTANGLE
                ? {
                      titleText: '',
                      subtitleTwoText: '',
                      subtitleText: '',
                      awardsScreenShape: settings.awardsScreenShape,
                      showExpiry: false
                  }
                : {
                      titleText: settings.titleText,
                      subtitleTwoText: settings.subtitle2,
                      subtitleText: settings.subtitle,
                      verticalAlignment: settings.valign,
                      showExpiry: settings.showExpiry,
                      textAlignment: settings.align,
                      textColour: rgbaToHex(settings.textColor, true),
                      ...(settings.showExpiry && {
                          expiryLabelAnchorStyle: deriveExpiryLabelAnchorStyle(
                              settings.expiryLabelAnchorStyleAlign,
                              settings.expiryLabelAnchorStyleVAlign
                          ),
                          expiryLabelDetailStyle: settings.expiryLabelDetailStyle
                      }),
                      ...(awardsScreenAwardEnabled && {
                          awardsScreenShape: AwardsScreenShapes.DOUBLE_RECTANGLE
                      })
                  })
        }
    };
};

const generateCreateClubVisualizationBody = (
    settings: AwardConfigureFormData,
    awardsScreenAwardEnabled?: boolean
) => {
    const opacity = getPerkOpacity(settings.backgroundColor);

    return {
        visualisation: {
            backgroundOverlayOpacity: opacity,
            backgroundOverlayColour: rgbaToHex(settings.backgroundColor, true),
            backgroundImageUrl: settings.image ?? '',
            ...(awardsScreenAwardEnabled && settings.awardsScreenShape !== AwardsScreenShapes.DOUBLE_RECTANGLE
                ? {
                      titleText: '',
                      subtitleTwoText: '',
                      subtitleText: '',
                      awardsScreenShape: settings.awardsScreenShape
                  }
                : {
                      titleText: settings.titleText,
                      subtitleTwoText: settings.subtitle2,
                      subtitleText: settings.subtitle,
                      verticalAlignment: settings.valign,
                      textAlignment: settings.align,
                      textColour: rgbaToHex(settings.textColor, true),
                      ...(awardsScreenAwardEnabled && {
                          awardsScreenShape: AwardsScreenShapes.DOUBLE_RECTANGLE
                      })
                  }),
            ...(settings.awardsScreenShape && {
                awardsScreenShape: settings.awardsScreenShape
            })
        }
    };
};

const generateCreateStampCardVisualizationBody = (
    settings: AwardConfigureFormData,
    awardsScreenAwardEnabled?: boolean
) => {
    const opacity = getPerkOpacity(settings.backgroundColor);
    return {
        visualisation: {
            textAlignment: settings.align,
            randomiseStampPositions: settings.randomizeStampCard,
            stampImageUrl: settings.stampImageUrl ?? '',
            stampColour: rgbaToHex(settings.stampColor, true),
            subtitleText: settings.subtitle,
            backgroundOverlayOpacity: opacity,
            backgroundOverlayColour: rgbaToHex(settings.backgroundColor, true),
            textColour: rgbaToHex(settings.textColor, true),
            backgroundImageUrl: settings.image ?? '',
            verticalAlignment: settings.valign,
            availableCardCopy: settings.availableCardCopy,
            nextCardProgressCopy: settings.nextCardProgressCopy,
            titleText: settings.titleText,
            ...(awardsScreenAwardEnabled && {
                awardsScreenShape: AwardsScreenShapes.DOUBLE_RECTANGLE
            })
        }
    };
};

export const generateCreateVisualizationBody = (
    settings: AwardConfigureFormData,
    awardsScreenAwardEnabled?: boolean
) => {
    if (settings.perkType === AwardTemplate.PSEUDO_CURRENCY) {
        return generateCreatePseudoAwardVisualizationBody(settings, awardsScreenAwardEnabled);
    }
    if (settings.perkType === AwardTemplate.STAMP_CARD) {
        return generateCreateStampCardVisualizationBody(settings, awardsScreenAwardEnabled);
    }
    if (settings.perkType === AwardTemplate.CLUB) {
        return generateCreateClubVisualizationBody(settings, awardsScreenAwardEnabled);
    }
    return generateCreateAdHocVisualizationBody(settings, awardsScreenAwardEnabled);
};

export const generateAwardConfigureFormData = (award: IAwardEnrichment): AwardConfigureFormData => {
    const colorArray =
        award.visualisation.backgroundOverlayColour &&
        hexToRgb(award.visualisation.backgroundOverlayColour).match(/\d+/g);
    const [r, g, b] = Array.isArray(colorArray) ? colorArray : [255, 255, 255];
    const rgba: RGBAValue = {
        r: Number(r),
        g: Number(g),
        b: Number(b),
        a: Number(award.visualisation.backgroundOverlayOpacity) || 0
    };

    let redemptionStrategy: 'none' | 'single' | 'multi' = 'none';
    if (isNumber(award.points?.redemption)) {
        redemptionStrategy = !award.points.redemption ? 'multi' : 'single';
    }

    const expiryLabelAlignment = award.visualisation.expiryLabelAnchorStyle
        ? deriveExpiryLabelAlignment(award.visualisation.expiryLabelAnchorStyle)
        : ({
              vertical: VerticalAlignment.BOTTOM,
              horizontal: TextAlignment.RIGHT
          } as const);
    const expiryLabelDetailStyle =
        award.visualisation.expiryLabelDetailStyle || AwardExpiryLabelDetailStyles.TOP_MIDDLE_CHIP;

    return {
        terms: award.visualisation.terms,
        perkType: award.template || AwardTemplate.AD_HOC,
        textColor: award.visualisation.textColour,
        image: award.visualisation.backgroundImageUrl,
        stampImageUrl: award.visualisation.stampImageUrl,
        stampColor: award.visualisation.stampColour,
        backgroundColor: rgbaToHex(rgba),
        align: award.visualisation.textAlignment,
        valign: award.visualisation.verticalAlignment,
        titleText: award.visualisation.titleText,
        subtitle: award.visualisation.subtitleText,
        subtitle2: award.visualisation.subtitleTwoText,
        randomizeStampCard: award.visualisation.randomiseStampPositions,
        availableCardCopy: award.visualisation.availableCardCopy,
        nextCardProgressCopy: award.visualisation.nextCardProgressCopy,
        showExpiry: award.visualisation.showExpiry,
        auto: award.auto,
        redemptionStrategy,
        ...(!!award.visualisation.showExpiry
            ? {
                  expiryLabelAnchorStyleAlign: expiryLabelAlignment.horizontal,
                  expiryLabelAnchorStyleVAlign: expiryLabelAlignment.vertical,
                  expiryLabelDetailStyle
              }
            : {}),
        awardsScreenShape: award.visualisation.awardsScreenShape,
        stampsPerCard: isDefined(award.points?.redemption) ? String(award.points.redemption) : ''
    };
};

export const generateAwardRedemptionFormData = (
    award: Omit<IAwardEnrichment, 'loyaltyRewardId' | 'loyaltyProvider' | 'id'>
): AwardRedemptionFormData => {
    let weekDayValues: string[] = [];
    if (award.dateTimeRestriction?.daysOfWeek) {
        weekDayValues = weekdays
            .filter(item => {
                const itemValue = Number(item.value);
                // eslint-disable-next-line no-bitwise
                return itemValue === (itemValue & award.dateTimeRestriction?.daysOfWeek);
            })
            .map(item => item.value);
    }

    return {
        token: award.token,
        discount: award.discount
            ? {
                  ...award.discount
              }
            : awardRedemptionDefaultFormValues.discount,
        basketQualification: award.basketQualification
            ? {
                  ...award.basketQualification
              }
            : awardRedemptionDefaultFormValues.basketQualification,
        dateTimeRestriction: award.dateTimeRestriction
            ? {
                  ...award.dateTimeRestriction,
                  startTime: award.dateTimeRestriction.startTime
                      ? convertSecondsToTime(award.dateTimeRestriction.startTime)
                      : undefined,
                  endTime: award.dateTimeRestriction.endTime
                      ? convertSecondsToTime(award.dateTimeRestriction.endTime)
                      : undefined,
                  startDate: award.dateTimeRestriction.startDate?.toString(),
                  endDate: award.dateTimeRestriction.endDate?.toString(),
                  daysOfWeek: weekDayValues
              }
            : awardRedemptionDefaultFormValues.dateTimeRestriction
    };
};

export const getAwardEnrichmentMutationData = (
    {
        basketQualification,
        discount,
        dateTimeRestriction,
        ...data
    }: AwardGeneralFormData & AwardConfigureFormData & AwardRedemptionFormData,
    loyaltyProvider: LoyaltyProvider,
    merchantTimezone?: string,
    awardsScreenAwardEnabled?: boolean,
    isEdit: boolean = false
): IAwardEnrichmentCreate => {
    const weekDays = dateTimeRestriction.daysOfWeek?.length
        ? dateTimeRestriction.daysOfWeek.reduce((acc, weekday) => {
              acc += Number(weekday);

              return acc;
          }, 0)
        : undefined;
    const hasDiscountFields = !!Object.values(discount).filter(Boolean).length;
    const hasBasketQualificationFields = !!Object.values(basketQualification).filter(
        item => isDefined(item) && !isEmptyString(item)
    ).length;

    let { startDate, endDate } = dateTimeRestriction;
    const startTimeMins = getMinsSinceMidnight(dateTimeRestriction.startTime) ?? 0;
    const endTimeMins = getMinsSinceMidnight(dateTimeRestriction.endTime) ?? 0;
    const zone = tz(merchantTimezone || 'UTC');
    if (startDate) {
        const d = setMinutes(
            setHours(new Date(startDate), Math.floor(startTimeMins / 60), { in: zone }),
            startTimeMins % 60,
            { in: zone }
        );
        startDate = new Date(d.getTime()).toISOString();
    }
    if (endDate) {
        const d = endTimeMins
            ? setMinutes(
                  setHours(new Date(endDate), Math.floor(endTimeMins / 60), { in: zone }),
                  endTimeMins % 60,
                  { in: zone }
              )
            : endOfDay(new Date(endDate), { in: zone });
        endDate = new Date(d.getTime()).toISOString();
    }

    let scopeMatchCode;
    if (discount.scope !== 'BASKET' && isString(discount.scopeMatchCode)) {
        scopeMatchCode = discount.scopeMatchCode
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
    }
    const isPseudoCurrency = data.perkType === AwardTemplate.PSEUDO_CURRENCY;
    let matchCode;
    if (!isPseudoCurrency && isString(basketQualification?.matchCode)) {
        matchCode = basketQualification.matchCode
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
    }

    const basketQualificationEmpty = isEdit ? {} : undefined;

    const body: IAwardEnrichmentCreate = {
        loyaltyProvider,
        loyaltyRewardId: data.loyaltyRewardId,
        title: data.title || undefined,
        token: isEmptyString(data.token) ? undefined : data.token,
        template: data.perkType,
        auto: isBoolean(data.auto) ? data.auto : undefined,
        basketQualification: hasBasketQualificationFields
            ? {
                  matchCode: matchCode || undefined,
                  minimumItems:
                      basketQualification.minimumItems ||
                      (!basketQualification.minimumValue && 1) ||
                      undefined,
                  minimumValue:
                      isDefined(basketQualification.minimumValue) &&
                      !isEmptyString(basketQualification.minimumValue)
                          ? basketQualification.minimumValue
                          : undefined,
                  scope: basketQualification.scope
              }
            : basketQualificationEmpty,
        discount: hasDiscountFields
            ? {
                  value: discount.value ? Number(discount.value) : undefined,
                  scope: discount.scope,
                  type: discount.type,
                  scopeMatchCode,
                  rule: discount.rule,
                  childItemRule: discount.childItemRule
              }
            : undefined,
        dateTimeRestriction: {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            startTime: startTimeMins * 60 || undefined,
            endTime: endTimeMins * 60 || undefined,
            daysOfWeek: weekDays
        },
        visualisation: {
            description: data.description || undefined,
            terms: data.terms || undefined,
            priority: data.order ? Number(data.order) : undefined,
            ...generateCreateVisualizationBody(data, awardsScreenAwardEnabled).visualisation
        }
    };
    if (data.perkType === AwardTemplate.AD_HOC || data.perkType === AwardTemplate.CLUB) {
        if (data.redemptionStrategy === 'multi') {
            body.points = {
                initial: 1,
                min: 1,
                max: 1,
                step: 0,
                redemption: 0
            };
        }
        if (data.redemptionStrategy === 'single') {
            body.points = {
                initial: 1,
                min: 0,
                max: 1,
                step: 1,
                redemption: 1
            };
        }
        if (data.redemptionStrategy === 'none' && isEdit) {
            body.points = {};
        }
    }
    if (data.perkType === AwardTemplate.STAMP_CARD) {
        const redemption = Number(data.stampsPerCard);
        if (redemption > 0 && !isEmptyString(data.stampsPerCard)) {
            body.points = {
                redemption
            };
        } else {
            body.points = {};
        }
    }
    return body;
};

export const generateSettingsFormData = (perk: Perk): PerkSettingsFormData => ({
    auto: perk.auto,
    terms: perk.terms,
    global: perk.isGlobal,
    enabled: perk.enabled ?? true,
    redemption: perk.token,
    locationIds: perk.locationIds || [],
    perkType: perk.template,
    multi: 'points' in perk && perk.points.redemption === 0,
    initial: perk.points.initial,
    ...(perk.template === AwardTemplate.PSEUDO_CURRENCY && {
        step: String(perk.points.step)
    }),
    showExpiry: !!perk.visualisation.showExpiry,
    maxTotalRedemptions: perk.points.maxTotalRedemptions,
    excludeDiscountIds: perk?.excludeDiscountIds || [],
    physicalStampsEnabled: perk.physicalStampsEnabled ?? false
});

export const generateVisualisationFormData = (perk: Perk): PerkVisualisationFormData => {
    const colorArray =
        perk.visualisation.backgroundOverlayColour &&
        hexToRgb(perk.visualisation.backgroundOverlayColour).match(/\d+/g);
    const [r, g, b] = Array.isArray(colorArray) ? colorArray : [255, 255, 255];
    const rgba: RGBAValue = {
        r: Number(r),
        g: Number(g),
        b: Number(b),
        a: Number(perk.visualisation.backgroundOverlayOpacity) || 0
    };

    const expiryLabelAlignment = perk.visualisation.expiryLabelAnchorStyle
        ? deriveExpiryLabelAlignment(perk.visualisation.expiryLabelAnchorStyle)
        : ({
              vertical: VerticalAlignment.BOTTOM,
              horizontal: TextAlignment.RIGHT
          } as const);

    return {
        perkType: perk.template,
        textColor: perk.visualisation.textColour,
        image: perk.visualisation.backgroundImageUrl,
        stampImageUrl: perk.visualisation.stampImageUrl,
        stampColor: perk.visualisation.stampColour,
        backgroundColor: rgbaToHex(rgba),
        align: perk.visualisation.textAlignment,
        valign: perk.visualisation.verticalAlignment,
        availableCardCopy: perk.visualisation.availableCardCopy,
        subtitle: perk.visualisation.subtitleText,
        subtitle2: perk.visualisation.subtitleTwoText,
        nextCardProgressCopy: perk.visualisation.nextCardProgressCopy,
        randomizeStampCard: perk.visualisation.randomiseStampPositions,
        stampsPerCard: String(perk.points.redemption),
        titleText: perk.visualisation.titleText,
        showExpiry: !!perk.visualisation.showExpiry,
        ...(perk.visualisation.showExpiry && {
            expiryLabelAnchorStyleAlign: expiryLabelAlignment.horizontal,
            expiryLabelAnchorStyleVAlign: expiryLabelAlignment.vertical,
            expiryLabelDetailStyle:
                perk.visualisation.expiryLabelDetailStyle || AwardExpiryLabelDetailStyles.TOP_MIDDLE_CHIP
        }),
        walletEnabled: perk.visualisation.wallet?.enabled ?? false,
        walletTitle: perk.visualisation.wallet?.title,
        walletPriority: perk.visualisation.wallet?.priority,
        walletDescription: perk.visualisation.wallet?.description,
        walletTerms: perk.visualisation.wallet?.terms,
        walletLink: perk.visualisation.wallet?.link,
        ...(perk.visualisation.wallet?.imageUrls?.length > 0 && {
            walletAppleImageUrl: perk.visualisation.wallet.imageUrls.find(
                ({ name }) => name === WalletPassType.APPLE
            )?.value,
            walletGoogleImageUrl: perk.visualisation.wallet.imageUrls.find(
                ({ name }) => name === WalletPassType.GOOGLE
            )?.value
        }),
        walletHidden: perk.visualisation.wallet?.hidden ?? false,
        walletDisplayPoints:
            perk.visualisation.wallet?.displayPoints ??
            [AwardTemplate.PSEUDO_CURRENCY, AwardTemplate.STAMP_CARD].includes(perk.template),
        awardsScreenShape: perk.visualisation.awardsScreenShape
    };
};
